import React from "react";
import { ChangeEvent, Fragment } from "react";
import { Link } from "react-router-dom";

import { apiGet } from "../../Api";
import { compareDates, formatDateTimeLocalNumeric, formatDateTimeRelativeLong, formatName } from "../../Utilities";
import withTitle from "../withTitle";

import HasRight from "../HasRight";
import ViewToolbar from "../ViewToolbar";

import "../../blocks.css";
import "../../icomoon.css";
import "../../list.css";
import "../../spinner.css";
import "../../table.css";
import "../../user.css";
import "../viewer.css";

interface IDeviceListViewState {
    isLoading: boolean;
    error?: string;
    devices: any[];
    filteredDevices: any[];
    searchText: string;
}

class DeviceListView extends React.Component<{}, IDeviceListViewState> {
    constructor(props: Readonly<{}>) {
        super(props);

        this.state = {
            devices: [],
            isLoading: false,

            filteredDevices: [],
            searchText: ""
        };

        this.changeSearchChange = this.changeSearchChange.bind(this);
    }

    public async componentDidMount() {
        try {
            this.setState({
                error: undefined,
                isLoading: true
            });
            const devices = await apiGet<any[]>("devices");

            this.setState({
                devices,
                filteredDevices: devices,
                isLoading: false,
            });
        }
        catch (e) {
            this.setState({
                error: e.message,
                isLoading: false
            });
        }
    }

    public render() {
        return (
            <div className="viewer-root">
                <ViewToolbar>
                    <HasRight right="device:create">
                        <Link to="/devices/add" className="icon-plus" />
                    </HasRight>
                </ViewToolbar>
                <div className="filter-control-box">
                    <input type="search" className="fill-space" placeholder="Search..." value={this.state.searchText} onChange={this.changeSearchChange} />
                </div>

                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th className="col-smallest">Assigned To</th>
                            <th className="col-smallest">Node ID</th>
                            <th className="col-smallest">Firmware</th>
                            <th className="col-smallest">Hardware</th>
                            <th className="col-smallest">Last Seen</th>
                            <th className="col-smallest" />
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Devices</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            this.state.isLoading ?
                                <tr>
                                    <td colSpan={10}>
                                        <div className="spinner-center lds-dual-ring" />
                                    </td>
                                </tr>
                                : this.state.filteredDevices.map(a => (
                                    <tr key={a.id}>
                                        <td>{a.name}</td>
                                        <td>{a.employee ? formatName(a.employee.name) : "-"}</td>
                                        <td className="inline" data-caption="Node ID: ">{a.nodeId}</td>
                                        <td className="inline" data-caption="Firmware: ">{a.softwareVersion || "-"}{a.targetVersion && !this.compareSoftwareVersion(a.softwareVersion, a.targetVersion) ? (<Fragment><br />{a.targetVersion} <i className="icon-cloud-download" title="Update queued" /></Fragment>) : ""}</td>
                                        <td className="inline" data-caption="Hardware: ">{a.hardwareId || "-"}</td>
                                        <td data-caption="Last Seen: " title={a.lastSeen ? formatDateTimeLocalNumeric(a.lastSeen) : ""} className={`inline ${this.lastSeenToClass(a.lastSeen)}`}>{a.lastSeen ? formatDateTimeRelativeLong(a.lastSeen) : "-"}</td>
                                        <td>
                                            <HasRight right="device:write">
                                                <Link to={`/devices/${a.id}`} className="button small text-with-icon">
                                                    <span>Details</span>
                                                    <i className="icon-chevron-right" />
                                                </Link>
                                            </HasRight>
                                        </td>
                                    </tr>
                                ))
                        }
                    </tbody>
                </table>
            </div>
        );
    }

    private changeSearchChange(e: ChangeEvent<HTMLInputElement>) {
        const searchValue = e.currentTarget.value;
        this.setState(s => {
            return {
                searchText: searchValue,

                filteredDevices: searchValue ? s.devices.filter(u =>
                    u.name.toLowerCase().includes(searchValue.toLowerCase()) ||
                    u.nodeId.toString().includes(searchValue.toLowerCase())
                ) : s.devices
            }
        });
    }

    private compareSoftwareVersion(a: string, target: string) {
        const dashIndex = target.indexOf("-");
        if (dashIndex > -1) {
            target = target.substring(0, dashIndex);
        }

        return a.localeCompare(target) === 0;
    }

    private lastSeenToClass(date: Date | string) {
        const diff = compareDates(new Date(), date);
        if (diff === 0) {
            return "";
        }

        if (diff <= 1 * 60 * 60) {
            return "time-active";
        }
        if (diff < 4 * 24 * 60 * 60) {
            return "";
        }

        return "time-old";
    }
}

export default withTitle("External Devices", DeviceListView);
